<div
  class="app-layout"
  [class.app-layout--immersive-host]="hideAppChrome()"
  [class.app-layout--offline]="!isOnline()"
  [class.app-layout--service-degraded]="serviceStatusBannerVisible()"
  [style.--app-footer-visible-offset.px]="footerVisibleOffset()"
>
  @if (!isOnline()) {
    <div class="app-offline-banner" role="alert">
      <mat-icon class="app-offline-banner__icon">cloud_off</mat-icon>
      <span i18n>Offline. Verbindung prüfen.</span>
      <button matButton class="app-offline-banner__retry" (click)="retryOnline()">
        <span i18n="@@app.offlineRetry">Nochmal versuchen</span>
      </button>
    </div>
  }
  @if (updateAvailable()) {
    <output class="app-update-banner" aria-live="polite" role="status">
      <div class="app-update-banner__inner">
        <span class="app-update-banner__icon-wrap" aria-hidden="true">
          <mat-icon class="app-update-banner__icon">system_update</mat-icon>
        </span>
        <div class="app-update-banner__copy">
          <strong class="app-update-banner__title" i18n="@@app.updateBanner.title"
            >Neue Version bereit</strong
          >
          <span class="app-update-banner__text" i18n="@@app.updateBanner.text"
            >Aktualisieren für den neuesten Stand.</span
          >
        </div>
        <button
          matButton="filled"
          class="app-update-banner__action"
          (click)="reloadWithUpdate()"
          [disabled]="updateReloading()"
          [attr.aria-busy]="updateReloading() ? 'true' : null"
          i18n="@@app.updateBanner.reloadNow"
        >
          Jetzt aktualisieren
        </button>
      </div>
    </output>
  }
  <ng-container connectionBannerHost></ng-container>
  <a
    href="#main-content"
    class="app-skip-link"
    [attr.inert]="isContentOverlayRoute() ? '' : null"
    (click)="skipToMainContent($event)"
    i18n
    >Zum Inhalt springen</a
  >

  <ng-container presetToastHost></ng-container>

  @if (presetSnackbarVisible()) {
    <output class="app-preset-snackbar" aria-live="polite">
      <span class="app-preset-snackbar__icon-wrap">
        <mat-icon class="app-preset-snackbar__icon">{{ presetSnackbarIcon() }}</mat-icon>
      </span>
      <span class="app-preset-snackbar__text">{{ presetSnackbarLabel() }}</span>
      <button
        type="button"
        class="app-preset-snackbar__action"
        (click)="openPresetCustomize()"
        i18n="@@app.snackbar.presetCustomize"
      >
        Anpassen
      </button>
      <button
        type="button"
        class="app-preset-snackbar__close"
        (click)="dismissPresetSnackbar()"
        i18n-aria-label="@@common.closeAria"
        aria-label="Schließen"
      >
        <mat-icon>close</mat-icon>
      </button>
    </output>
  }
  @if (installSnackbarVisible()) {
    <output class="app-install-snackbar" aria-live="polite">
      <span class="app-install-snackbar__icon-wrap">
        <mat-icon class="app-install-snackbar__icon">get_app</mat-icon>
      </span>
      <span class="app-install-snackbar__text" i18n>App installieren und schneller starten</span>
      <button
        type="button"
        class="app-install-snackbar__action"
        (click)="triggerInstall()"
        i18n="@@app.snackbar.pwaInstall"
      >
        Installieren
      </button>
      <button
        type="button"
        class="app-install-snackbar__close"
        (click)="dismissInstallSnackbar()"
        i18n-aria-label="@@common.closeAria"
        aria-label="Schließen"
      >
        <mat-icon>close</mat-icon>
      </button>
    </output>
  }

  @if (!hideAppChrome()) {
    <app-top-toolbar
      [attr.inert]="isContentOverlayRoute() ? '' : null"
      [scrolled]="hasScrolled()"
      [hideOnScroll]="true"
      [toolbarHidden]="toolbarHidden()"
      (focusin)="showToolbarForFocus()"
    />
  }
  @if (serviceStatusBannerVisible()) {
    <div
      class="app-service-status-banner"
      role="status"
      [class.app-service-status-banner--busy]="footerStatusColor() === 'yellow'"
      [class.app-service-status-banner--critical]="footerStatusColor() === 'red'"
    >
      <span class="app-service-status-banner__text" i18n="@@app.serviceStatusBanner.text"
        >⚠️ Mögliche Verzögerungen.</span
      >
      <button
        type="button"
        class="app-service-status-banner__link"
        (click)="openServerStatusFromBanner()"
        i18n="@@app.serviceStatusBanner.learnMore"
      >
        Mehr erfahren
      </button>
    </div>
  }

  <main
    id="main-content"
    class="app-main app-main--toolbar-fixed"
    [class.app-main--immersive-host]="hideAppChrome()"
    [class.app-main--present]="isPresentRoute()"
    [class.app-main--toolbar-hidden]="toolbarHidden() && !hideAppChrome()"
    role="main"
    tabindex="0"
  >
    <div
      class="app-main__content"
      [class.app-main__content--centered]="
        !isPreviewRoute() && !isFeedbackRoute() && !hideAppChrome()
      "
    >
      <router-outlet />
    </div>
  </main>
  @if (footerVisible()) {
    <footer
      #appFooter
      class="app-footer"
      role="contentinfo"
      [attr.inert]="isContentOverlayRoute() ? '' : null"
    >
      <div class="app-footer__inner">
        <div class="app-footer__row">
          <div
            class="app-footer__links"
            [class.app-footer__links--offline]="footerShowApiOffline()"
            [attr.data-footer-api-state]="
              footerHealthCheckDone() ? (apiStatus() ? 'ok' : 'offline') : 'pending'
            "
          >
            @if (isOnline()) {
              <a
                matButton
                class="app-footer__primary app-footer__link--external"
                [href]="infoLandingFeaturesUrl()"
                target="_blank"
                rel="noopener noreferrer"
                i18n-aria-label="@@app.footer.infoLandingAria"
                aria-label="Was arsnova.eu kann (öffnet in neuem Tab)"
              >
                <span class="app-footer__primary-stack">
                  <mat-icon class="app-footer__icon" aria-hidden="true">info</mat-icon>
                  <span class="app-footer__link-text" i18n="@@app.footer.infoLanding"
                    >Was arsnova.eu kann</span
                  >
                </span>
              </a>
            } @else {
              <button
                matButton
                type="button"
                class="app-footer__primary app-footer__link--external app-footer__link--offline"
                disabled
                i18n-aria-label="@@app.footer.infoLandingOfflineAria"
                aria-label="Was arsnova.eu kann – derzeit offline nicht erreichbar"
              >
                <span class="app-footer__primary-stack">
                  <mat-icon class="app-footer__icon" aria-hidden="true">info</mat-icon>
                  <span class="app-footer__link-text" i18n="@@app.footer.infoLanding"
                    >Was arsnova.eu kann</span
                  >
                </span>
              </button>
            }
            <a
              matButton
              class="app-footer__primary"
              data-footer-focus="footer-help"
              [routerLink]="localizedPath('/help')"
              i18n-aria-label="@@app.footer.helpOpenAria"
              aria-label="So funktioniert’s"
            >
              <span class="app-footer__primary-stack">
                <mat-icon class="app-footer__icon" aria-hidden="true">menu_book</mat-icon>
                <span class="app-footer__link-text" i18n="@@app.footer.help"
                  >So funktioniert’s</span
                >
              </span>
            </a>
            <button
              matButton
              type="button"
              class="app-footer__primary"
              #footerMoreButton
              #footerMoreTrigger="matMenuTrigger"
              [matMenuTriggerFor]="footerMoreMenu"
              (menuClosed)="onFooterMoreMenuClosed()"
              data-footer-focus="footer-more"
              i18n-aria-label="@@app.footer.moreOpenAria"
              aria-label="Mehr"
            >
              <span class="app-footer__primary-stack">
                <!-- more_vert: projektweit etabliertes More-Icon (Menu-Trigger); nicht more_horiz -->
                <mat-icon class="app-footer__icon" aria-hidden="true">more_vert</mat-icon>
                <span class="app-footer__link-text" i18n="@@app.footer.more">Mehr</span>
              </span>
            </button>
            @if (footerShowApiOffline()) {
              <button
                matButton
                class="app-footer__status-action"
                (click)="retryApiConnection()"
                [disabled]="apiRetrying()"
              >
                {{ footerRetryLabel() }}
              </button>
            }
          </div>
        </div>
      </div>
      <!-- Außerhalb von row/links: Host darf Flex-/Grid-Verteilung nicht beeinflussen. -->
      <mat-menu
        #footerMoreMenu="matMenu"
        class="app-footer__more-menu app-footer__more-menu-panel"
        yPosition="above"
        xPosition="before"
      >
        <a
          mat-menu-item
          [routerLink]="localizedPath('/legal/imprint')"
          i18n-aria-label="@@app.footer.imprintOpenAria"
          aria-label="Impressum"
        >
          <mat-icon class="app-footer__icon" aria-hidden="true">gavel</mat-icon>
          <span i18n="@@app.footer.imprint">Impressum</span>
        </a>
        <a
          mat-menu-item
          [routerLink]="localizedPath('/legal/privacy')"
          i18n-aria-label="@@app.footer.privacyOpenAria"
          aria-label="Datenschutz"
        >
          <mat-icon class="app-footer__icon" aria-hidden="true">privacy_tip</mat-icon>
          <span i18n="@@app.footer.privacy">Datenschutz</span>
        </a>
        <a
          mat-menu-item
          [routerLink]="localizedPath('/legal/accessibility')"
          i18n-aria-label="@@app.footer.accessibilityOpenAria"
          aria-label="Barrierefreiheit"
        >
          <mat-icon class="app-footer__icon" aria-hidden="true">accessibility</mat-icon>
          <span i18n="@@app.footer.accessibility">Barrierefreiheit</span>
        </a>
        @if (serverStatusWidgetVisible()) {
          <button
            mat-menu-item
            type="button"
            (click)="openServerStatusFromMore()"
            i18n-aria-label="@@app.footer.statusHelpTriggerAria"
            aria-label="Betriebsstatus öffnen: Statusanzeige und Systemlast-Indikatoren"
          >
            <mat-icon
              class="app-footer__status-dot"
              [class.app-footer__status-dot--healthy]="footerStatusColor() === 'green'"
              [class.app-footer__status-dot--busy]="footerStatusColor() === 'yellow'"
              [class.app-footer__status-dot--overloaded]="footerStatusColor() === 'red'"
              [class.app-footer__status-dot--unknown]="footerStatusColor() === 'gray'"
              [style.color]="footerStatusDotCssColor()"
              aria-hidden="true"
              >lens</mat-icon
            >
            <span i18n="@@app.footer.serverHelpLabel">Betriebsstatus</span>
          </button>
        }
      </mat-menu>
    </footer>
  }
</div>
